import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2, Lock, User, AlertTriangle, CheckCircle,
  Eye, EyeOff, Mail, RefreshCw, Shield, Loader2, Check, X,
} from "lucide-react";
import { tenantService } from "../services/tenantService";
import { showError, showSuccess } from "../components/Alerts";

type Phase = "validating" | "form" | "success" | "error";

// ─── OTP Input ──────────────────────────────────────────────────────────────
function OtpInput({ value, onChange, disabled, hasError }: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  hasError?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? "");

  const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = digits.map((d, idx) => (idx === i ? "" : d)).join("");
      onChange(next.trimEnd());
      if (i > 0) refs.current[i - 1]?.focus();
    }
  };

  const handleChange = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, "").slice(-1);
    const next = digits.map((d, idx) => (idx === i ? char : d)).join("");
    onChange(next);
    if (char && i < 5) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      onChange(pasted);
      refs.current[Math.min(pasted.length - 1, 5)]?.focus();
    }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          disabled={disabled}
          className={`
            w-11 h-13 text-center text-xl font-bold rounded-xl border-2
            bg-background text-foreground
            focus:outline-none focus:ring-2 transition-all duration-150
            disabled:opacity-40 disabled:cursor-not-allowed
            ${hasError
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/20"
              : d
                ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 focus:border-indigo-500 focus:ring-indigo-500/20"
                : "border-border focus:border-indigo-500 focus:ring-indigo-500/20"
            }
          `}
        />
      ))}
    </div>
  );
}

const isOtpComplete = (code: string) => /^\d{6}$/.test(code);

// ─── Password strength ───────────────────────────────────────────────────────
function getPasswordScore(pw: string): number {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8)           score++;
  if (pw.length >= 12)          score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^A-Za-z0-9]/.test(pw))  score++;
  return score;
}

const STRENGTH_LABELS = ["", "Muy débil", "Débil", "Regular", "Fuerte", "Muy fuerte"];
const STRENGTH_BAR    = ["", "bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-blue-500", "bg-emerald-500"];
const STRENGTH_TEXT   = ["", "text-red-600 dark:text-red-400", "text-orange-600 dark:text-orange-400", "text-yellow-600 dark:text-yellow-400", "text-blue-600 dark:text-blue-400", "text-emerald-600 dark:text-emerald-400"];

// ─── Componente principal ────────────────────────────────────────────────────
export default function AcceptInvitationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [phase, setPhase] = useState<Phase>("validating");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // OTP
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Formulario
  const [form, setForm] = useState({ name: "", password: "", confirmPassword: "", phoneNumber: "", profilePictureUrl: "", bio: "" });
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const pwScore = getPasswordScore(form.password);
  const pwChecks = {
    length:    form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    number:    /[0-9]/.test(form.password),
    special:   /[^A-Za-z0-9]/.test(form.password),
    match:     form.password.length > 0 && form.password === form.confirmPassword,
  };

  // ── Guard StrictMode ────────────────────────────────────────────────────────
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    if (!token) {
      setPhase("error");
      setErrorMsg("El link de invitación es inválido o está incompleto.");
      return;
    }

    tenantService.validateInviteToken(token)
      .then(res => {
        setMaskedEmail(res.maskedEmail);
        setTenantName(res.tenantName);
        setPhase("form");
        startCooldown();
      })
      .catch(err => {
        const msg = err?.response?.data?.message || err?.message
          || "El token de invitación es inválido o ya fue usado.";
        setPhase("error");
        setErrorMsg(msg);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startCooldown = (s = 60) => {
    setResendCooldown(s);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    try {
      await tenantService.validateInviteToken(token);
      setOtpCode("");
      setOtpError("");
      startCooldown();
      showSuccess("Nuevo código enviado a " + maskedEmail);
    } catch {
      showError("No se pudo reenviar el código. Intentá de nuevo.");
    } finally {
      setResending(false);
    }
  };

  // ── Submit: verifica OTP + crea usuario en un solo paso atómico ────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones cliente
    const errs: Record<string, string> = {};
    if (!isOtpComplete(otpCode)) errs.otp = "Ingresá el código de 6 dígitos.";
    if (!form.name.trim()) errs.name = "El nombre es obligatorio.";
    if (!form.password || form.password.length < 8) errs.password = "Mínimo 8 caracteres.";
    if (form.password !== form.confirmPassword) errs.confirmPassword = "Las contraseñas no coinciden.";
    if (Object.keys(errs).length) { setFormErrors(errs); return; }

    setSubmitting(true);
    setFormErrors({});
    setOtpError("");

    try {
      const response = await tenantService.acceptInvitation({
        token,
        name: form.name.trim(),
        password: form.password,
        confirmPassword: form.confirmPassword,
        otpCode,
        phoneNumber: form.phoneNumber,
        profilePictureUrl: form.profilePictureUrl,
        bio: form.bio,
      });

      if (response.token) {
        setPhase("success");
        showSuccess("¡Cuenta creada con éxito!");
        setTimeout(() => navigate("/login?registered=true"), 2200);
      } else {
        showError("Error inesperado. Intentá de nuevo.");
      }
    } catch (err: any) {
      const msg: string = err?.response?.data?.message || err?.message || "Error al crear la cuenta.";

      // Errores de OTP → resaltar el campo OTP y limpiar el código
      if (/código|otp|expirado|intentos/i.test(msg)) {
        setOtpError(msg);
        setOtpCode("");
      } else {
        setFormErrors({ general: msg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Left Panel ────────────────────────────────────────────────────────────
  const LeftPanel = () => (
    <div className="relative hidden overflow-hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(99,102,241,0.25) 0%, transparent 55%),
                          radial-gradient(circle at 75% 75%, rgba(99,102,241,0.15) 0%, transparent 55%)`,
      }} />
      <div className="absolute w-32 h-32 border top-20 left-16 border-indigo-500/20 rounded-2xl rotate-12 animate-pulse" />
      <div className="absolute w-24 h-24 border bottom-32 right-20 border-indigo-400/15 rounded-xl -rotate-12" />
      <div className="relative z-10 flex flex-col justify-center px-16">
        <div className="flex items-center gap-4 mb-10">
          <div className="flex items-center justify-center border w-14 h-14 rounded-2xl bg-indigo-500/20 border-indigo-500/30">
            <Building2 className="text-indigo-400 w-7 h-7" />
          </div>
          <div>
            <p className="text-3xl font-bold tracking-tight text-white">PruneStone</p>
            <p className="text-sm font-medium text-indigo-300/80">Construction Management</p>
          </div>
        </div>
        <h2 className="mb-6 text-4xl font-bold leading-tight text-white">
          {tenantName ? "Unite a " : "Bienvenido al "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
            {tenantName || "PruneStone"}
          </span>
        </h2>
        <p className="max-w-md text-lg leading-relaxed text-slate-400">
          Completá tu registro para comenzar a usar el sistema de gestión.
        </p>
      </div>
    </div>
  );

  // ── Fase: validando ────────────────────────────────────────────────────────
  if (phase === "validating") return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted-foreground border-t-indigo-600" />
        <p className="text-muted-foreground">Verificando invitación...</p>
      </div>
    </div>
  );

  // ── Fase: error fatal ──────────────────────────────────────────────────────
  if (phase === "error") return (
    <div className="flex min-h-screen">
      <LeftPanel />
      <div className="flex items-center justify-center flex-1 px-6 py-12 bg-background">
        <div className="w-full max-w-md text-center space-y-5">
          <div className="flex items-center justify-center h-16 w-16 rounded-full bg-red-50 dark:bg-red-950/30 mx-auto">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Invitación inválida</h2>
          <p className="text-muted-foreground text-sm">{errorMsg}</p>
          <a href="/login" className="inline-block text-sm font-medium text-indigo-600 hover:underline">
            Ir al inicio de sesión
          </a>
        </div>
      </div>
    </div>
  );

  // ── Fase: éxito ────────────────────────────────────────────────────────────
  if (phase === "success") return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 dark:bg-emerald-950/30 mx-auto animate-bounce">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">¡Cuenta creada!</h2>
        <p className="text-muted-foreground">Redirigiendo al inicio de sesión...</p>
      </div>
    </div>
  );

  // ── Fase: formulario (OTP + datos juntos) ──────────────────────────────────
  return (
    <div className="flex min-h-screen">
      <LeftPanel />
      <div className="flex items-center justify-center flex-1 px-6 py-12 bg-background overflow-y-auto">
        <div className="w-full max-w-md">

          {/* Logo móvil */}
          <div className="flex items-center justify-center gap-3 mb-8 lg:hidden">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <span className="text-xl font-bold text-foreground">PruneStone</span>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-foreground">Completá tu registro</h2>
            {tenantName && (
              <p className="mt-1 text-sm text-muted-foreground">
                Uniéndote a <strong className="text-foreground">{tenantName}</strong>
              </p>
            )}
          </div>

          {/* Error general */}
          {formErrors.general && (
            <div className="flex items-start gap-2 px-3 py-2.5 mb-4 border border-red-200 rounded-xl bg-red-50 dark:bg-red-950/30">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{formErrors.general}</p>
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit} noValidate>

            {/* ── Email (fijo, solo lectura) ────────────────────────────── */}
            <div className="space-y-1.5">
              <label htmlFor="acc-email" className="text-sm font-medium text-foreground">
                Email de invitación
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  id="acc-email"
                  type="email"
                  value={maskedEmail}
                  readOnly
                  tabIndex={-1}
                  className="w-full pl-10 pr-10 py-3 text-sm border border-border rounded-xl bg-muted/50 text-foreground cursor-not-allowed select-none"
                />
                <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60 pointer-events-none" />
              </div>
              <p className="text-xs text-muted-foreground">El código de verificación fue enviado a este correo.</p>
            </div>

            {/* ── Nombre ────────────────────────────────────────────────── */}
            <div className="space-y-1.5">
              <label htmlFor="acc-name" className="text-sm font-medium text-foreground">
                Nombre completo <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  id="acc-name"
                  type="text"
                  placeholder="Tu nombre completo"
                  value={form.name}
                  onChange={e => { setForm(p => ({ ...p, name: e.target.value })); setFormErrors(p => { const n = {...p}; delete n.name; return n; }); }}
                  disabled={submitting}
                  maxLength={100}
                  autoComplete="name"
                  className={`w-full pl-10 pr-4 py-3 text-sm transition-all duration-200 border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed ${formErrors.name ? "border-red-400 focus:ring-red-500/30 focus:border-red-400" : "border-border"}`}
                />
              </div>
              {formErrors.name && <p className="text-xs text-red-600">{formErrors.name}</p>}
            </div>

            {/* ── Teléfono ────────────────────────────────────────────────── */}
            <div className="space-y-1.5">
              <label htmlFor="acc-phone" className="text-sm font-medium text-foreground">
                Teléfono <span className="text-muted-foreground font-normal">(opcional)</span>
              </label>
              <div className="relative">
                <input
                  id="acc-phone"
                  type="text"
                  placeholder="Ej: +5491112345678"
                  value={form.phoneNumber}
                  onChange={e => setForm(p => ({ ...p, phoneNumber: e.target.value }))}
                  disabled={submitting}
                  maxLength={50}
                  className="w-full px-4 py-3 text-sm transition-all duration-200 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* ── Foto de perfil (URL) ────────────────────────────────────────────────── */}
            <div className="space-y-1.5">
              <label htmlFor="acc-avatar" className="text-sm font-medium text-foreground">
                URL de foto de perfil <span className="text-muted-foreground font-normal">(opcional)</span>
              </label>
              <div className="relative">
                <input
                  id="acc-avatar"
                  type="url"
                  placeholder="https://ejemplo.com/foto.jpg"
                  value={form.profilePictureUrl}
                  onChange={e => setForm(p => ({ ...p, profilePictureUrl: e.target.value }))}
                  disabled={submitting}
                  maxLength={500}
                  className="w-full px-4 py-3 text-sm transition-all duration-200 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* ── Bio ────────────────────────────────────────────────── */}
            <div className="space-y-1.5">
              <label htmlFor="acc-bio" className="text-sm font-medium text-foreground">
                Biografía / Notas <span className="text-muted-foreground font-normal">(opcional)</span>
              </label>
              <div className="relative">
                <textarea
                  id="acc-bio"
                  placeholder="Contanos sobre vos..."
                  value={form.bio}
                  onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                  disabled={submitting}
                  rows={2}
                  className="w-full px-4 py-3 text-sm transition-all duration-200 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                />
              </div>
            </div>

            {/* ── Contraseña ────────────────────────────────────────────── */}
            <div className="space-y-1.5">
              <label htmlFor="acc-pw" className="text-sm font-medium text-foreground">
                Contraseña <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  id="acc-pw"
                  type={showPw ? "text" : "password"}
                  placeholder="Mínimo 8 caracteres"
                  value={form.password}
                  onChange={e => { setForm(p => ({ ...p, password: e.target.value })); setFormErrors(p => { const n = {...p}; delete n.password; delete n.confirmPassword; return n; }); }}
                  disabled={submitting}
                  autoComplete="new-password"
                  className={`w-full pl-10 pr-10 py-3 text-sm transition-all duration-200 border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed ${formErrors.password ? "border-red-400 focus:ring-red-500/30 focus:border-red-400" : "border-border"}`}
                />
                <button type="button" onClick={() => setShowPw(p => !p)} tabIndex={-1}
                  aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.password && (
                <>
                  <div className="space-y-1.5 mt-2" aria-live="polite">
                    <div className="flex gap-1" aria-hidden="true">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= pwScore ? STRENGTH_BAR[pwScore] : "bg-muted"}`} />
                      ))}
                    </div>
                    {pwScore > 0 && (
                      <p className={`text-xs font-medium ${STRENGTH_TEXT[pwScore]}`}>
                        Seguridad: {STRENGTH_LABELS[pwScore]}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mt-3" aria-label="Requisitos de contraseña">
                    {[
                      { ok: pwChecks.length,    label: "Mínimo 8 caracteres" },
                      { ok: pwChecks.uppercase, label: "Una mayúscula" },
                      { ok: pwChecks.number,    label: "Un número" },
                      { ok: pwChecks.special,   label: "Un carácter especial" },
                    ].map(({ ok, label }) => (
                      <div key={label} className="flex items-center gap-1.5">
                        {ok
                          ? <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" aria-hidden="true" />
                          : <X     className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                        }
                        <span className={`text-xs ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {formErrors.password && <p className="text-xs text-red-600">{formErrors.password}</p>}
            </div>

            {/* ── Confirmar contraseña ──────────────────────────────────── */}
            <div className="space-y-1.5">
              <label htmlFor="acc-confirm" className="text-sm font-medium text-foreground">
                Confirmar contraseña <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  id="acc-confirm"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Repetí tu contraseña"
                  value={form.confirmPassword}
                  onChange={e => { setForm(p => ({ ...p, confirmPassword: e.target.value })); setFormErrors(p => { const n = {...p}; delete n.confirmPassword; return n; }); }}
                  disabled={submitting}
                  autoComplete="new-password"
                  aria-invalid={(form.confirmPassword.length > 0 && !pwChecks.match) || undefined}
                  className={`w-full pl-10 pr-10 py-3 text-sm transition-all duration-200 border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    form.confirmPassword && !pwChecks.match
                      ? "border-red-400 focus:ring-red-500/30 focus:border-red-400"
                      : form.confirmPassword && pwChecks.match
                        ? "border-emerald-400 focus:ring-emerald-500/30 focus:border-emerald-400"
                        : "border-border focus:ring-primary/50 focus:border-primary"
                  }`}
                />
                <button type="button" onClick={() => setShowConfirm(p => !p)} tabIndex={-1}
                  aria-label={showConfirm ? "Ocultar confirmación" : "Mostrar confirmación"}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.confirmPassword && (
                <div className="flex items-center gap-1.5" aria-live="polite">
                  {pwChecks.match ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" aria-hidden="true" />
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">Las contraseñas coinciden</span>
                    </>
                  ) : (
                    <>
                      <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0" aria-hidden="true" />
                      <span className="text-xs text-red-600 dark:text-red-400">Las contraseñas no coinciden</span>
                    </>
                  )}
                </div>
              )}
              {formErrors.confirmPassword && <p className="text-xs text-red-600">{formErrors.confirmPassword}</p>}
            </div>

            {/* ── Sección OTP ─────────────────────────────────────────── */}
            <div className="p-4 border rounded-xl border-border bg-muted/20 space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <p className="text-sm font-semibold text-foreground">Código de verificación</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Ingresá el código de 6 dígitos enviado a{" "}
                <span className="font-medium text-foreground">{maskedEmail}</span>.
                También revisá spam.
              </p>

              <OtpInput
                value={otpCode}
                onChange={v => { setOtpCode(v); setOtpError(""); setFormErrors(p => { const n = {...p}; delete n.otp; return n; }); }}
                disabled={submitting}
                hasError={!!(otpError || formErrors.otp)}
              />

              {(otpError || formErrors.otp) && (
                <div className="flex items-center gap-2 px-3 py-2 border border-red-200 rounded-lg bg-red-50 dark:bg-red-950/30">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{otpError || formErrors.otp}</p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                  <Mail className="w-3.5 h-3.5" />
                  <span>Expira en 10 min</span>
                </div>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || resending}
                  className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resending
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Enviando...</>
                    : resendCooldown > 0
                      ? `Reenviar en ${resendCooldown}s`
                      : <><RefreshCw className="w-3 h-3" /> Reenviar código</>}
                </button>
              </div>
            </div>

            {/* ── Submit ────────────────────────────────────────────────── */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 mt-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2 focus:ring-offset-background"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando cuenta...</>
                : <><CheckCircle className="w-4 h-4" /> Completar registro</>}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            ¿Ya tenés cuenta?{" "}
            <a href="/login" className="font-medium text-indigo-600 hover:underline">Iniciá sesión aquí</a>
          </p>
        </div>
      </div>
    </div>
  );
}
